import classNames from "classnames"
import React, { useContext, useRef } from "react"

import { Experiment } from "../../../../constants/experiments"
import { Color } from "../../../../constants/V2/color"
import { useIsExperiment } from "../../../../hooks/experiments"
import AddToCartButton from "../../../elements/V2/Buttons/AddToCartButton"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import TextButton from "../../../elements/V2/Buttons/TextButton"
import Callout from "../../../elements/V2/Callout"
import GatsbyStoryblokImage from "../../../elements/V2/GatsbyStoryblokImage"
import NumberDisplay from "../../../elements/V2/NumberDisplay"
import Section from "../../../elements/V2/Section"
import StickyContainer from "../../../elements/V2/StickyContainer"
import Typography from "../../../elements/V2/Typography"
import { NavigationV2Context } from "../../Navigation/context"

import { PurchasableItem } from "@hooks/V2/storyblok"
import { useElementObserver } from "@hooks/V2/useElementObserver"
import { getBackgroundColorClass, getAccentColor } from "@utils/V2/color"
import { useIsBreakpoint } from "@utils/V2/screen"

type Props = {
  headingText: string
  headingSize?: "display1" | "h1" | "h2" | "h3" | "h4" | "h5"
  headingMobileAlignment?: "left" | "center"
  bodyText: string
  priceValue?: string | number
  priceSuperscriptText?: string
  priceSidescriptText?: string
  priceSubscriptText?: string
  backgroundColor: Color
  primaryButtonText?: string
  primaryButtonLink?: string
  primaryButtonMobileLink?: string
  primaryButtonBorderColor: Color
  secondaryButtonText: string
  secondaryButtonLink: string
  imageUrl: string
  imageAlt: string
  isPurchasable?: boolean
  purchasableItem: PurchasableItem
  iconUrl?: string
  callout?: Storyblok.Callout
  reasonNotAvailable?: string
  waitlistLink?: string
  hasScrollArrow?: boolean
  secondaryTrackingEvent: string
  secondaryTrackingEventKey?: string
  secondaryTrackingEventValue?: string
} & Partial<Storyblok.CTATrackingEvent>

const HalfHero = ({
  headingText,
  headingSize = "h3",
  headingMobileAlignment = "left",
  bodyText,
  priceValue,
  priceSubscriptText,
  priceSidescriptText,
  priceSuperscriptText,
  backgroundColor,
  primaryButtonText,
  primaryButtonLink,
  primaryButtonMobileLink,
  primaryButtonBorderColor,
  secondaryButtonText,
  secondaryButtonLink,
  imageUrl,
  imageAlt,
  iconUrl,
  isPurchasable = false,
  purchasableItem,
  callout,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,
  reasonNotAvailable,
  waitlistLink,
  hasScrollArrow = false,
  secondaryTrackingEvent,
  secondaryTrackingEventKey,
  secondaryTrackingEventValue,
  ...props
}: Props) => {
  const isProductHeroExperiment = useIsExperiment(Experiment.PRODUCT_HERO_CTA)
  const isScrollLinksExperiment = useIsExperiment(Experiment.SCROLL_LINKS)

  const navigationContext = useContext(NavigationV2Context)
  const textContainerRef = useRef(null)
  const { isVisible: isCtaVisible, startObserving } =
    useElementObserver(textContainerRef)
  const accentColor = getAccentColor(backgroundColor)
  const isMobile = useIsBreakpoint("mobile")

  if (isMobile) {
    startObserving()
  }

  return (
    <div
      className={classNames(
        getBackgroundColorClass(backgroundColor),
        "-mb-lg pb-lg grid grid-cols-2 items-center",
        { "sm:max-h-[85dvh] xl:max-h-[90dvh]": isScrollLinksExperiment }
      )}
      {...props}
    >
      <Section
        className={classNames(
          "col-span-full flex flex-col gap-16 pb-24 pt-72 sm:col-start-1 sm:col-end-2 sm:pb-72 md:gap-32 lg:gap-40"
        )}
      >
        <div
          className={classNames(
            "flex flex-col gap-24",
            headingMobileAlignment === "left"
              ? "items-start"
              : "items-center text-center"
          )}
        >
          {callout && (
            <Callout
              color={getAccentColor(backgroundColor)}
              text={callout.text}
              highlightedWord={callout.highlightedWord}
            />
          )}
          <Typography
            text={headingText}
            font="grotesk"
            weight="medium"
            size={headingSize}
            color={accentColor}
            element="h1"
          />
          <Typography
            text={bodyText}
            font="grotesk"
            weight="book"
            size="body-lg"
            color={accentColor}
          />
        </div>
        <NumberDisplay
          type="price"
          size="large"
          adornmentTextPosition="left"
          adornmentText="R"
          textColor={accentColor}
          value={priceValue}
          sideScriptText={priceSidescriptText}
          subscriptText={priceSubscriptText}
          strikeSuperscriptText={true}
          superscriptText={priceSuperscriptText}
        />
        <div
          ref={textContainerRef}
          className="flex flex-col items-center gap-24 sm:flex-row sm:items-baseline"
        >
          {isPurchasable && !isProductHeroExperiment ? (
            <AddToCartButton
              style="solid"
              purchasableItem={purchasableItem}
              buttonColor={primaryButtonBorderColor}
              reasonNotAvailable={reasonNotAvailable}
              waitlistLink={waitlistLink}
            />
          ) : primaryButtonText && primaryButtonLink ? (
            <PillButton
              size="large"
              style="solid"
              text={primaryButtonText}
              color={primaryButtonBorderColor}
              linkUrl={primaryButtonLink}
              mobileLinkUrl={primaryButtonMobileLink}
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
              hasScrollArrow={hasScrollArrow}
            />
          ) : null}
          <TextButton
            style="text"
            text={secondaryButtonText}
            linkUrl={secondaryButtonLink}
            color={accentColor}
            trackingEvent={secondaryTrackingEvent}
            trackingEventKey={secondaryTrackingEventKey}
            trackingEventValue={secondaryTrackingEventValue}
          />
        </div>
        <StickyContainer
          isVisible={
            typeof isCtaVisible === "boolean" &&
            !isCtaVisible &&
            !navigationContext.isMobileMenuOpen
          }
        >
          {isPurchasable && !isProductHeroExperiment ? (
            <AddToCartButton
              style="solid"
              purchasableItem={purchasableItem}
              buttonColor={Color.Blue}
              reasonNotAvailable={reasonNotAvailable}
              waitlistLink={waitlistLink}
            />
          ) : primaryButtonText && primaryButtonLink ? (
            <PillButton
              size="large"
              style="solid"
              text={primaryButtonText}
              color={primaryButtonBorderColor}
              linkUrl={primaryButtonLink}
              mobileLinkUrl={primaryButtonMobileLink}
              trackingEvent={trackingEvent}
              trackingEventKey={trackingEventKey}
              trackingEventValue={trackingEventValue}
            />
          ) : null}
        </StickyContainer>
        {iconUrl && (
          <GatsbyStoryblokImage image={iconUrl} alt={imageAlt} width={400} />
        )}
      </Section>
      <>
        <GatsbyStoryblokImage
          className={classNames(
            "col-span-full mb-0 mt-40 sm:col-start-2 sm:col-end-3 sm:my-80 md:my-96",
            { rounded: isScrollLinksExperiment }
          )}
          height={isScrollLinksExperiment ? 900 : undefined}
          loadType="eager"
          image={imageUrl}
          alt={imageAlt || "Hero image"}
          quality={80}
        />
      </>
    </div>
  )
}

export default HalfHero
